import { useCallback, useMemo, useState } from "react";
import { Env } from "../../shared";
import { NotificationAudience, RecurrenceDay, Deal } from "../../deals/api/useDeals";
import { CreateDealRequest } from "../../deals/api/useCreateDeals";
import { PickedBusiness } from "../shared/BusinessPicker";
import { defaultRecurrenceEndDate } from "../shared/recurrence";
import { toIso } from "../shared/timeConversion";
import { useAdminFlag } from "../../deals/featureFlags";

export interface DealForm {
  business: PickedBusiness | null;
  title: string;
  description: string;
  startTime: string; // datetime-local
  endTime: string;   // datetime-local
  notificationAudience: NotificationAudience[];
  recurrenceEnabled: boolean;
  recurrenceDays: RecurrenceDay[];
  recurrenceHasEndDate: boolean;
  recurrenceEndDate: string;
}

export function blankDealForm(): DealForm {
  return {
    business: null,
    title: "",
    description: "",
    startTime: "",
    endTime: "",
    notificationAudience: [],
    recurrenceEnabled: false,
    recurrenceDays: [],
    recurrenceHasEndDate: false,
    recurrenceEndDate: "",
  };
}

/** Build a fresh draft prefilled from an existing deal (duplicate-from-recent).
 *  Business + times are intentionally left blank for the admin to confirm. */
export function draftFromDeal(deal: Deal): DealForm {
  return {
    ...blankDealForm(),
    title: deal.title,
    description: deal.description ?? "",
    notificationAudience: deal.notification_audience ?? [],
    recurrenceEnabled: (deal.recurrence_days?.length ?? 0) > 0,
    recurrenceDays: deal.recurrence_days ?? [],
    recurrenceHasEndDate: Boolean(deal.recurrence_end_date),
    recurrenceEndDate: deal.recurrence_end_date ?? "",
  };
}

/**
 * Deal form state. `initial` is used only on mount (standard useState semantics).
 * To repopulate after mount (e.g. duplicate-from-recent), call `reset(draft)`.
 */
export function useDealFormState(initial?: DealForm) {
  const [form, setForm] = useState<DealForm>(initial ?? blankDealForm());
  const RECURRING_DEALS_ENABLED = useAdminFlag('recurring-deals-enabled');

  const setField = useCallback(<K extends keyof DealForm>(field: K, value: DealForm[K]) => {
    setForm(f => ({ ...f, [field]: value }));
  }, []);

  const reset = useCallback((next?: DealForm) => setForm(next ?? blankDealForm()), []);

  const canSubmit = useMemo(() => {
    const recurrenceValid =
      !RECURRING_DEALS_ENABLED ||
      !form.recurrenceEnabled ||
      (form.recurrenceDays.length > 0 && (!form.recurrenceHasEndDate || Boolean(form.recurrenceEndDate)));
    return (
      Boolean(form.business) &&
      Boolean(form.title.trim()) &&
      Boolean(form.description.trim()) &&
      Boolean(form.startTime) &&
      Boolean(form.endTime) &&
      form.notificationAudience.length > 0 &&
      new Date(form.endTime) > new Date(form.startTime) &&
      recurrenceValid
    );
  }, [form, RECURRING_DEALS_ENABLED]);

  const buildPayload = useCallback((environment: Env): CreateDealRequest => {
    if (!form.business) {
      throw new Error("buildPayload requires a selected business");
    }
    const tz = form.business.timezone ?? "UTC";
    const payload: CreateDealRequest = {
      environment,
      businessId: form.business.id,
      title: form.title.trim(),
      description: form.description.trim(),
      startTime: toIso(form.startTime, tz),
      endTime: toIso(form.endTime, tz),
      notificationAudience: form.notificationAudience,
    };
    if (RECURRING_DEALS_ENABLED) {
      const recurring = form.recurrenceEnabled && form.recurrenceDays.length > 0;
      // The backend gates recurrence on `isRecurring`; recurrenceDays alone is ignored.
      payload.isRecurring = recurring;
      if (recurring) {
        payload.recurrenceDays = form.recurrenceDays;
        // Never null. The series generator selects parents with
        // `recurrence_end_date >= CURRENT_DATE`, and NULL fails that comparison
        // — so a blank end date does not mean "runs forever", it means the
        // series silently generates nothing at all. Defaulting to the end of
        // the starting year gives the deal a real lifespan instead.
        payload.recurrenceEndDate = form.recurrenceHasEndDate
          ? form.recurrenceEndDate
          : defaultRecurrenceEndDate(form.startTime, form.business.timezone ?? "UTC");
      }
    }
    return payload;
  }, [form, RECURRING_DEALS_ENABLED]);

  return { form, setField, reset, canSubmit, buildPayload };
}
